import { FC, FormEvent, ReactNode, useId } from "react";

import Stack from "@mui/material/Stack";
import { Locales, OrganizationCountry } from "@simplyk/common";

import { TranslationColumnHeader } from "../sharedComponents/TranslationColumnHeader";
import { useTranslationActivity } from "../Context/TranslationActivityContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { TranslationLineLabelsContext } from "@/components/TranslationLine/TranslationLineLabelsContext";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ArrowTwoSides, Language } from "@/icons/outlined";

interface TranslationModalLayoutProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onSwap: () => void;
  formLocale: Locales;
  translationLocale: Locales;
  organizationCountry?: OrganizationCountry;
  isTranslating?: boolean;
  isSaving?: boolean;
  actionsDisabled?: boolean;
  inline?: boolean;
  slots: {
    content: ReactNode;
  };
}

export const TranslationModalLayout: FC<TranslationModalLayoutProps> = ({
  open,
  onClose,
  onSubmit,
  onSwap,
  formLocale,
  translationLocale,
  organizationCountry,
  isTranslating = false,
  isSaving = false,
  actionsDisabled = false,
  inline = false,
  slots,
}) => {
  const { t } = useTranslate();
  const { isTranslating: hasPendingTranslations } = useTranslationActivity();
  const titleId = useId();
  const swapDescriptionId = useId();
  const isBusy = isTranslating || hasPendingTranslations || isSaving;
  const languageHeaders: [ReactNode, ReactNode] = [
    <TranslationColumnHeader key="main" role="main" locale={formLocale} organizationCountry={organizationCountry} />,
    <TranslationColumnHeader
      key="secondary"
      role="secondary"
      locale={translationLocale}
      organizationCountry={organizationCountry}
    />,
  ];

  return (
    <DashboardDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      inline={inline}
      ref={(element) => {
        const dialog = element?.matches('[role="dialog"]') ? element : element?.querySelector('[role="dialog"]');
        dialog?.setAttribute("aria-labelledby", titleId);
      }}
    >
      <Stack
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          if (isBusy || actionsDisabled) {
            event.preventDefault();
            return;
          }
          void onSubmit(event);
        }}
        sx={{ minHeight: 0 }}
        data-test="translation-dialog"
      >
        <DashboardDialogBody
          slots={{
            header: (
              <DashboardDialogHeader
                icon={<Language />}
                title={t("dashboard", "translation.modal.title")}
                slotProps={{
                  title: { id: titleId, component: "h2" },
                  closeButton: {
                    "aria-label": t("common", "close"),
                    sx: (theme) => ({ width: theme.spacing(4.75) }),
                  },
                }}
                sx={(theme) => ({
                  padding: theme.spacing(3, 2, 1.5, 3),
                  flexShrink: 0,
                  borderBlockEnd: 0,
                })}
              />
            ),
            content: (
              <DashboardDialogContent sx={(theme) => ({ padding: theme.spacing(0, 3, 3) })}>
                <Stack gap={2}>
                  <Stack
                    direction="row"
                    gap={3}
                    sx={{ display: { xs: "none", md: "flex" }, "& > *": { flex: 1, minWidth: 0 } }}
                  >
                    {languageHeaders}
                  </Stack>
                  <TranslationLineLabelsContext.Provider value={languageHeaders}>
                    {slots.content}
                  </TranslationLineLabelsContext.Provider>
                </Stack>
              </DashboardDialogContent>
            ),
            footer: (
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                gap={2}
                sx={(theme) => ({
                  padding: theme.spacing(2),
                  flexShrink: 0,
                  backgroundColor: theme.palette.surface.neutral.white,
                  borderTop: `1px solid ${theme.palette.border.neutral.quiet}`,
                  borderEndStartRadius: "inherit",
                  borderEndEndRadius: "inherit",
                })}
                data-test="translation-dialog-actions"
              >
                <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
                  <DashboardButton
                    type="button"
                    variant="text"
                    vibe="neutral"
                    endIcon={<ArrowTwoSides />}
                    onClick={onSwap}
                    disabled={isBusy || actionsDisabled}
                    aria-describedby={swapDescriptionId}
                    sx={{ alignSelf: "flex-start" }}
                    data-test="translation-swap-button"
                  >
                    {t("dashboard", "translation.modal.swap")}
                  </DashboardButton>
                  <DashboardTypography id={swapDescriptionId} variant="body2" vibe="text-neutral-moderate">
                    {t("dashboard", "translation.modal.swapDescription")}
                  </DashboardTypography>
                </Stack>
                <Stack
                  direction="row"
                  gap={1}
                  sx={{ alignSelf: { md: "flex-start" }, flexShrink: 0, "& > *": { flex: { xs: 1, md: "initial" } } }}
                >
                  <DashboardButton type="button" variant="outlined" vibe="neutral" onClick={onClose}>
                    {t("dashboard", "translation.modal.cancel")}
                  </DashboardButton>
                  <DashboardButton
                    type="submit"
                    variant="filled"
                    vibe="brand"
                    aria-label={t("dashboard", "translation.modal.save")}
                    disabled={isBusy || actionsDisabled}
                    isLoading={isBusy}
                  >
                    {t("dashboard", "translation.modal.save")}
                  </DashboardButton>
                </Stack>
              </Stack>
            ),
          }}
        />
      </Stack>
    </DashboardDialog>
  );
};
